import { createORPCClient } from '@orpc/client'
import type { ContractRouterClient } from '@orpc/contract'
import { OpenAPILink } from '@orpc/openapi-client/fetch'
import { createTanstackQueryUtils } from '@orpc/tanstack-query'
import { baseContract } from 'pixai-contract/api/orpc'
import { bypassAdmin } from 'src/stores/admin'
import { getApiUrl } from 'src/utils/config'
import { getFingerprint } from 'src/utils/fingerprint'
import {
  setIntercomUserJwt,
  unsetIntercomUserJwt,
} from 'src/utils/intercom/storage'
import { getToken, setToken } from 'src/utils/token'
import { setIPCountry } from '../helpers/ip'

/**
 * Per-call client context for the contract-based oRPC client.
 *
 * `headers` lets callers attach one-off request headers (e.g. the
 * `X-Turnstile-Token` issued by the captcha widget for `createFromMedia`).
 * Pass via the second argument of any procedure call:
 *
 * ```ts
 * await contractClient.artwork.createFromMedia(input, {
 *   context: { headers: { 'X-Turnstile-Token': token } },
 * })
 * ```
 */
export interface ContractClientContext {
  headers?: Record<string, string>
}

// Contract-driven oRPC client. Unlike `./client.ts` (which wraps the
// `@hey-api/openapi-ts` runtime against a server-generated spec), this client
// is built directly from the shared `baseContract` exported by
// `pixai-contract`. Frontend code paths for endpoints whose contract already
// lives in `pixai-contract` should prefer this client — no codegen round
// trip through `pixai-server` is involved.

type Variant = 'authed' | 'public'

/**
 * Build a fetch wrapper that mirrors the request/response interceptor logic
 * from `./client.ts` so behaviour stays identical across the two clients
 * during the migration. Specifically:
 *
 *   - **request**: attaches `Authorization` (when a token is present),
 *     `X-Bypass-Admin`, and `X-Browser-ID` headers; sets `credentials`
 *     according to the variant (logged-in requests must `include` cookies;
 *     public/cacheable endpoints must `omit` them so the CDN can serve the
 *     same response to everyone).
 *   - **response**: refreshes the local token from the `token` header,
 *     wipes the token on 401 *only* when the failing request actually carried
 *     auth (so a stray unauthenticated probe can't clobber a freshly stored
 *     token from a concurrent login), and forwards intercom JWT / country
 *     code headers to their stores.
 */
function buildFetch(variant: Variant) {
  const credentials: RequestCredentials =
    variant === 'authed' ? 'include' : 'omit'

  return async (
    request: Request,
    init: RequestInit,
    options: { context?: ContractClientContext },
  ): Promise<Response> => {
    const headers = new Headers(request.headers)

    const token = variant === 'authed' ? getToken() : null
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    if (variant === 'authed' && bypassAdmin()) {
      headers.set('X-Bypass-Admin', '1')
    }

    const browserId = getFingerprint()
    if (browserId) {
      headers.set('X-Browser-ID', browserId)
    }

    // Per-call headers (e.g. X-Turnstile-Token) supplied via
    // `client.<op>(input, { context: { headers } })`.
    if (options.context?.headers) {
      for (const [key, value] of Object.entries(options.context.headers)) {
        headers.set(key, value)
      }
    }

    const finalRequest = new Request(request, { headers })
    const response = await fetch(finalRequest, { ...init, credentials })

    if (variant === 'authed') {
      if (response.status === 401) {
        if (token) {
          // Only wipe when the failing request actually carried a token.
          // Otherwise an unauthenticated request that lands after a sibling
          // login flow has just stored a fresh token would clobber it.
          setToken('')
          unsetIntercomUserJwt()
        }
      } else {
        const refreshed = response.headers.get('token')
        if (refreshed) {
          setToken(refreshed)
        }
      }
    }

    const intercomUserJwt = response.headers.get('x-intercom-user-jwt')
    if (intercomUserJwt) {
      setIntercomUserJwt(intercomUserJwt)
    }

    const ipCountry = response.headers.get('x-country-code')
    if (ipCountry) {
      setIPCountry(ipCountry)
    }

    return response
  }
}

// `pixai-server` mounts the oRPC router under `/v2` (see
// `pixai-server/src/api/orpc/router.ts`'s `.prefix('/v2')`), but pixai-contract
// only carries the per-namespace prefixes (e.g. `/artwork`). The OpenAPILink
// builds the full URL from `url + contract path`, so the mount prefix has to
// live on the base URL here — without it the request lands on `/artwork/...`,
// misses the oRPC handler, falls through to Apollo, and 400s with a CSRF
// prevention error.
const baseUrl = `${getApiUrl().replace(/\/$/, '')}/v2`

const authedLink = new OpenAPILink<ContractClientContext>(baseContract, {
  url: baseUrl,
  fetch: buildFetch('authed'),
})

const publicLink = new OpenAPILink<ContractClientContext>(baseContract, {
  url: baseUrl,
  fetch: buildFetch('public'),
})

export const contractClient: ContractRouterClient<
  typeof baseContract,
  ContractClientContext
> = createORPCClient(authedLink)

/** Client for public, cacheable endpoints — no credentials or auth headers. */
export const contractPublicClient: ContractRouterClient<
  typeof baseContract,
  ContractClientContext
> = createORPCClient(publicLink)

/**
 * Tanstack-query utils bound to the authed client. Mirrors how the rest of
 * the app uses the codegen'd `*Options` helpers, but the call graph runs
 * `useQuery(orpc.artwork.getSimilarArtworks.queryOptions({ input }))`
 * rather than `useQuery(artworkGetSimilarArtworksOptions({ client, ... }))`.
 */
export const orpc = createTanstackQueryUtils(contractClient)

/** Same as `orpc`, but routed through the no-credentials public client. */
export const orpcPublic = createTanstackQueryUtils(contractPublicClient)
